import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaUser,
  FaLock,
  FaEnvelope,
  FaIdBadge,
  FaTruck,
  FaBuilding,
  FaUserShield,
  FaSignature,
  FaSave,
  FaTimes,
  FaCloudUploadAlt,
  FaPen,
  FaTrash,
} from "react-icons/fa";
import "./AddUser.css";

const AddUserForm = ({ isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    userrole: "user",
    badge: "",
    fleetNo: "",
    signature: null,
    signatureType: "upload",
    contractor_name: "",
    aws_url: null,
    userId: null,
  });
  const [message, setMessage] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [isUserSaved, setIsUserSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFetchedDetails, setHasFetchedDetails] = useState(false);
  const [lineWidth, setLineWidth] = useState(2);
  const [lineColor, setLineColor] = useState("#000000");
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (isEditing && id && !hasFetchedDetails) {
      fetchUserDetails(id);
      setHasFetchedDetails(true);
    }
    setupCanvas();
  }, [isEditing, id, hasFetchedDetails, lineWidth, lineColor]);

  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage("");
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [message]);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = lineColor;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const normalizeVal = (v) => {
    if (v === undefined || v === null) return null;
    if (typeof v === "string") {
      const t = v.trim();
      if (t === "" || t.toLowerCase() === "null") return null;
      return t;
    }
    return v;
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let rec = response.data;
      if (Array.isArray(rec)) rec = rec[0] || {};
      if (rec && rec.data) rec = rec.data;

      const normalized = {};
      Object.keys(rec || {}).forEach((k) => {
        normalized[k.toLowerCase()] = normalizeVal(rec[k]);
      });

      setFormData({
        name: normalized.name || "",
        username: normalized.username || "",
        email: normalized.email || "",
        password: normalized.password || "",
        userrole: normalized.userrole || "user",
        badge: normalized.badge || "",
        fleetNo: normalized.fleetno || "",
        contractor_name: normalized.contractor_name || "",
        signature: null,
        signatureType: "upload",
        aws_url: normalized.aws_url || null,
        userId: normalized.user_id || userId,
      });
      setIsUserSaved(true);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, signature: e.target.files[0] }));
  };

  const handleDeleteSignature = async () => {
    if (window.confirm("Are you sure you want to delete this signature?")) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(
          `${process.env.REACT_APP_BASE_API_URL}/delete-signature/${id || formData.userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFormData((prev) => ({ ...prev, aws_url: null }));
        alert("Signature deleted successfully!");
      } catch (error) {
        console.error("Error deleting signature:", error);
        alert("Failed to delete signature.");
      }
    }
  };

  const handleSignatureTypeChange = (type) => {
    setFormData((prev) => ({ ...prev, signatureType: type, signature: null }));
  };

  const getCanvasCoordinates = (canvas, clientX, clientY) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const { x, y } = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(canvas, touch.clientX, touch.clientY);
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const { x, y } = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handleTouchMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(canvas, touch.clientX, touch.clientY);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.closePath();
    setIsDrawing(false);
  };

  const handleTouchEnd = () => {
    endDrawing();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const dataURLToBlob = (dataURL) => {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const saveCanvasImage = async (userId) => {
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width * 2;
    tempCanvas.height = canvas.height * 2;
    const tempCtx = tempCanvas.getContext("2d");
    tempCtx.scale(2, 2);
    tempCtx.drawImage(canvas, 0, 0);

    const dataURL = tempCanvas.toDataURL("image/png");
    const blob = dataURLToBlob(dataURL);
    const file = new File([blob], "signature.png", { type: "image/png" });

    try {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("signature", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/upload-signature/${userId}`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        aws_url: response.data.data,
      }));

      alert("Signature saved and uploaded successfully!");
      setTimeout(() => {
        navigate("/add-user");
      }, 3000);
    } catch (error) {
      console.error("Error uploading signature:", error);
      alert("Failed to upload signature.");
    }
  };

  const handleUploadSignature = async (userId) => {
    if (!formData.signature) {
      alert("Please choose a file before uploading.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("signature", formData.signature);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/upload-signature/${userId}`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        aws_url: uploadResponse.data.data,
      }));

      alert("Signature uploaded successfully!");
      setTimeout(() => {
        navigate("/add-user");
      }, 3000);
    } catch (error) {
      console.error("Error uploading signature:", error);
      alert("Failed to upload signature.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.contractor_name || !formData.contractor_name.trim()) {
      alert("Please select Contractor Name (required).");
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      let userId;

      if (isEditing) {
        await axios.patch(
          `${process.env.REACT_APP_BASE_API_URL}/users/${id}`,
          {
            name: formData.name,
            username: formData.username,
            email: formData.email,
            userrole: formData.userrole,
            badge: formData.badge || null,
            fleetNo: formData.fleetNo || null,
            contractor_name: formData.contractor_name || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMessage("User updated successfully!");
        userId = id;
        setIsUserSaved(true);
        setTimeout(() => navigate("/add-user"), 1500);
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/add-user`,
          {
            name: formData.name,
            username: formData.username,
            email: formData.email,
            password: formData.password,
            userrole: formData.userrole,
            badge: formData.badge || null,
            fleetNo: formData.fleetNo || null,
            contractor_name: formData.contractor_name || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMessage("User added successfully!");
        userId = response.data.userId;
        setIsUserSaved(true);
      }

      setFormData((prev) => ({ ...prev, userId }));
    } catch (error) {
      console.error("Error saving user:", error);
      setMessage("Failed to save user.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="user-form-page">
      <div className="user-form-card">
        {/* Header with Arrow Back button */}
        <div className="user-form-header">
          <div className="user-form-header-left">
            <button
              type="button"
              className="bt-back-btn"
              onClick={() => navigate("/add-user")}
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h2 className="user-form-title">
                {isEditing ? "Edit User Account" : "Create New User Account"}
              </h2>
              <p className="user-form-subtitle">
                Configure operational permissions, role access, and verification credentials
              </p>
            </div>
          </div>
        </div>

        {/* User Account Form */}
        <form onSubmit={handleSubmit}>
          <div className="user-form-grid">
            {/* Full Name */}
            <div className="user-form-field">
              <label className="user-form-label">
                Full Name <span className="required-star">*</span>
              </label>
              <input
                type="text"
                className="user-form-input"
                name="name"
                placeholder="e.g. Salim Al-Habsi"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Username */}
            <div className="user-form-field">
              <label className="user-form-label">
                Login / Username <span className="required-star">*</span>
              </label>
              <input
                type="text"
                className="user-form-input"
                name="username"
                placeholder="e.g. salim.habsi"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div className="user-form-field">
              <label className="user-form-label">
                Email Address <span className="required-star">*</span>
              </label>
              <input
                type="email"
                className="user-form-input"
                name="email"
                placeholder="e.g. user@pdo.co.om"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Password */}
            <div className="user-form-field">
              <label className="user-form-label">
                Password {!isEditing && <span className="required-star">*</span>}
              </label>
              <input
                type="password"
                className="user-form-input"
                name="password"
                placeholder={isEditing ? "Leave blank to keep current password" : "Enter account password"}
                value={formData.password}
                onChange={handleChange}
                required={!isEditing}
              />
            </div>

            {/* Role */}
            <div className="user-form-field">
              <label className="user-form-label">
                Assigned Role <span className="required-star">*</span>
              </label>
              <select
                className="user-form-select"
                name="userrole"
                value={formData.userrole}
                onChange={handleChange}
                required
              >
                <option value="admin">Admin</option>
                <option value="technician">Technician</option>
                <option value="jm">JM (Journey Manager)</option>
                <option value="coo">COO (Operations Officer)</option>
                <option value="driver">Driver</option>
                <option value="driver_supv">Driver Supervisor</option>
                <option value="oc">OC (Operations Coordinator)</option>
              </select>
            </div>

            {/* Contractor */}
            <div className="user-form-field">
              <label className="user-form-label">
                Contractor Name <span className="required-star">*</span>
              </label>
              <select
                className="user-form-select"
                name="contractor_name"
                value={formData.contractor_name}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Contractor --</option>
                <option value="AMLS">AMLS</option>
                <option value="Al Mukhtar">Al Mukhtar</option>
              </select>
            </div>

            {/* Driver Specific Fields */}
            {formData.userrole === "driver" && (
              <div className="driver-details-section">
                <h4 className="driver-section-heading">Driver Fleet Profile</h4>
                <div className="user-form-field">
                  <label className="user-form-label">
                    Driver Civil ID / Badge <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    className="user-form-input"
                    name="badge"
                    placeholder="e.g. 10482931"
                    value={formData.badge}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="user-form-field">
                  <label className="user-form-label">Assigned Fleet No</label>
                  <input
                    type="text"
                    className="user-form-input"
                    name="fleetNo"
                    placeholder="e.g. TR-5421"
                    value={formData.fleetNo}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* Signature Section */}
            <div className="user-signature-card">
              <div className="signature-card-header">
                <h4>
                  <FaSignature style={{ marginRight: "8px", color: "#2563eb" }} />
                  Digital Signature Verification
                </h4>
                <div className="signature-type-toggles">
                  <button
                    type="button"
                    className={`sig-toggle-btn ${formData.signatureType === "upload" ? "active" : ""}`}
                    onClick={() => handleSignatureTypeChange("upload")}
                    disabled={!!formData.aws_url || !isUserSaved}
                  >
                    <FaCloudUploadAlt style={{ marginRight: "4px" }} /> Upload File
                  </button>
                  <button
                    type="button"
                    className={`sig-toggle-btn ${formData.signatureType === "draw" ? "active" : ""}`}
                    onClick={() => handleSignatureTypeChange("draw")}
                    disabled={!!formData.aws_url || !isUserSaved}
                  >
                    <FaPen style={{ marginRight: "4px" }} /> Draw Signature
                  </button>
                </div>
              </div>

              {!isUserSaved && (
                <div className="signature-note">
                  Save user credentials first. The digital signature section will activate immediately afterward.
                </div>
              )}

              {formData.aws_url ? (
                <div className="signature-preview-area">
                  <img
                    src={`${formData.aws_url}?t=${new Date().getTime()}`}
                    alt="User Signature"
                    className="signature-preview-img"
                  />
                  <button
                    type="button"
                    className="sig-delete-btn"
                    onClick={handleDeleteSignature}
                  >
                    <FaTrash style={{ marginRight: "6px" }} /> Delete Signature
                  </button>
                </div>
              ) : formData.signatureType === "upload" && isUserSaved ? (
                <div className="signature-preview-area">
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    onChange={handleFileChange}
                    id="user-sig-file"
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="user-sig-file"
                    className="user-back-btn"
                    style={{ cursor: "pointer" }}
                  >
                    <FaCloudUploadAlt /> Choose Signature Image
                  </label>
                  {formData.signature && (
                    <span style={{ fontSize: "13px", color: "#2563eb", fontWeight: 600 }}>
                      Selected: {formData.signature.name}
                    </span>
                  )}
                  <button
                    type="button"
                    className="sig-save-btn"
                    onClick={() => handleUploadSignature(formData.userId || id)}
                    disabled={!formData.signature}
                  >
                    Upload Signature
                  </button>
                </div>
              ) : formData.signatureType === "draw" && isUserSaved ? (
                <div className="sig-canvas-wrap">
                  <div className="sig-controls-bar">
                    <span>Line Width:</span>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={lineWidth}
                      onChange={(e) => setLineWidth(e.target.value)}
                    />
                    <span>Color:</span>
                    <input
                      type="color"
                      value={lineColor}
                      onChange={(e) => setLineColor(e.target.value)}
                    />
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={420}
                    height={180}
                    className="sig-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={endDrawing}
                    onMouseLeave={endDrawing}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  />
                  <div className="sig-canvas-actions">
                    <button type="button" className="sig-clear-btn" onClick={clearCanvas}>
                      Clear Canvas
                    </button>
                    <button
                      type="button"
                      className="sig-save-btn"
                      onClick={() => saveCanvasImage(formData.userId || id)}
                    >
                      Save Signature
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Action Buttons */}
            <div className="user-form-actions">
              <button
                type="button"
                className="user-cancel-btn"
                onClick={() => navigate("/add-user")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="user-submit-btn"
                disabled={isSubmitting || (!isEditing && isUserSaved)}
              >
                <FaSave />
                <span>
                  {isSubmitting
                    ? "Saving..."
                    : isEditing
                    ? "Update User Account"
                    : "Save User Account"}
                </span>
              </button>
            </div>

            {/* Feedback Message */}
            {message && (
              <div
                className={`user-form-alert ${
                  message.toLowerCase().includes("success") ? "success" : "error"
                }`}
              >
                {message}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserForm;
